<template>
  <div>
    <!-- Loading -->
    <template v-if="loading">
      <TopBarTitleComponent title="Cargando..." :showBackIcon="true" />
      <b-container fluid class="pb-4 px-lg-4 pt-4">
        <SkeletonLoadingCard />
      </b-container>
    </template>

    <template v-else-if="document">
      <TopBarTitleComponent :title="document.name" :showBackIcon="true">
        <template slot="right-area">
          <div class="d-flex align-items-center" style="gap:8px">
            <button
              class="btn md__btn-third-secondary"
              v-if="document.status === 'draft'"
              @click="goEdit"
            >
              <i class="el-icon-edit" /> Editar
            </button>
            <button
              class="btn md__btn-third-secondary"
              v-if="
                ['signed', 'downloaded', 'owner_signed'].includes(
                  document.status
                )
              "
              @click="downloadPdf"
            >
              <i class="el-icon-download" /> Descargar PDF
            </button>
            <button
              class="btn md__btn-third-secondary"
              v-if="
                ['sent', 'viewed', 'partially_signed'].includes(document.status)
              "
              @click="cancelDocument"
            >
              <i class="el-icon-circle-close" /> Cancelar
            </button>
            <ButtonPrimaryComponent
              v-if="document.status === 'owner_signed' && signers.length"
              title="Enviar a firmar"
              @onSaveClick="openSendReview"
            />
          </div>
        </template>
      </TopBarTitleComponent>

      <b-container fluid class="pb-4 px-lg-4 pt-4">
        <b-row>
          <!-- Columna izquierda: Timeline de actividad -->
          <b-col lg="7" xl="8">
            <!-- Resumen del documento -->
            <div class="doc-detail-summary mb-4">
              <div class="doc-detail-summary__row">
                <span class="doc-detail-summary__label">Estado</span>
                <span
                  class="doc-badge"
                  :class="'doc-badge--' + document.status"
                >
                  {{ readStatus(document.status) }}
                </span>
              </div>
              <div class="doc-detail-summary__row">
                <span class="doc-detail-summary__label">Tipo</span>
                <span class="doc-detail-summary__value">{{
                  readType(document.type)
                }}</span>
              </div>
              <div class="doc-detail-summary__row">
                <span class="doc-detail-summary__label">Creado</span>
                <span class="doc-detail-summary__value">{{
                  document.created_at
                }}</span>
              </div>
              <div class="doc-detail-summary__row" v-if="document.expires_at">
                <span class="doc-detail-summary__label">Expira</span>
                <span class="doc-detail-summary__value">{{
                  document.expires_at
                }}</span>
              </div>
              <div class="doc-detail-summary__row" v-if="document.quote">
                <span class="doc-detail-summary__label">Cotización</span>
                <span class="doc-detail-summary__value"
                  >#{{ document.quote.code }}</span
                >
              </div>
              <div class="doc-detail-summary__row" v-if="document.source">
                <span class="doc-detail-summary__label">Origen</span>
                <span class="doc-detail-summary__value">{{
                  readSource(document.source)
                }}</span>
              </div>
            </div>

            <!-- Progreso de firmas -->
            <div
              class="doc-detail-progress mb-4"
              v-if="document.signers && document.signers.length"
            >
              <p class="doc-detail-progress__title">Progreso de firmas</p>
              <div class="doc-detail-progress__bar-wrapper">
                <div class="doc-detail-progress__bar">
                  <div
                    class="doc-detail-progress__fill"
                    :style="{ width: signProgress + '%' }"
                  />
                </div>
                <span class="doc-detail-progress__text">
                  {{ signedCount }} de {{ document.signers.length }} firmados
                </span>
              </div>
            </div>

            <!-- Vista previa del documento -->
            <div
              class="doc-preview-panel mb-4"
              v-if="document.content_html || document.source === 'upload'"
            >
              <div class="doc-preview-panel__header">
                <p class="doc-preview-panel__title">Vista previa</p>
                <button
                  class="doc-preview-panel__toggle"
                  @click="showPreview = !showPreview"
                >
                  {{ showPreview ? "Ocultar" : "Mostrar" }}
                  <i
                    :class="
                      showPreview ? 'el-icon-arrow-up' : 'el-icon-arrow-down'
                    "
                  />
                </button>
              </div>
              <div v-show="showPreview">
                <!-- HTML content -->
                <div
                  class="doc-preview-panel__html"
                  v-if="document.content_html"
                  v-html="document.content_html"
                />
                <!-- PDF preview -->
                <div
                  class="doc-preview-panel__pdf"
                  v-else-if="document.source === 'upload'"
                >
                  <div v-if="pdfPages.length">
                    <div
                      class="doc-preview-panel__page"
                      v-for="(page, idx) in pdfPages"
                      :key="idx"
                    >
                      <canvas :ref="'pdfCanvas' + idx" />
                    </div>
                  </div>
                  <div
                    v-else-if="loadingPdf"
                    class="doc-preview-panel__loading"
                  >
                    <i class="el-icon-loading" /> Cargando PDF...
                  </div>
                  <div v-else class="doc-preview-panel__empty">
                    <i class="el-icon-document" />
                    <p>No se pudo cargar la vista previa del PDF.</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Timeline -->
            <div class="doc-timeline mb-4">
              <p class="doc-timeline__title">Actividad</p>
              <div v-if="auditLogs.length" class="doc-timeline__list">
                <div
                  class="doc-timeline__item"
                  v-for="log in auditLogs"
                  :key="log.id"
                >
                  <div
                    class="doc-timeline__dot"
                    :class="getTimelineDotClass(log.action)"
                  />
                  <div class="doc-timeline__content">
                    <p class="doc-timeline__action">
                      {{ log.read_action || log.action }}
                    </p>
                    <p class="doc-timeline__meta">
                      {{ log.created_at }}
                      <template v-if="log.user_name">
                        · {{ log.user_name }}</template
                      >
                      <template v-if="log.ip"> · {{ log.ip }}</template>
                    </p>
                  </div>
                </div>
              </div>
              <p v-else class="doc-timeline__empty">
                Sin actividad registrada.
              </p>
            </div>
          </b-col>

          <!-- Columna derecha: Firmantes -->
          <b-col lg="5" xl="4">
            <!-- Firmantes -->
            <div class="doc-signers-panel">
              <div class="doc-signers-panel__header">
                <p class="doc-signers-panel__title">Firmantes</p>
                <button
                  class="doc-signers-panel__add"
                  v-if="document.status === 'draft'"
                  @click="showAddSigner = true"
                >
                  <i class="el-icon-plus" /> Agregar
                </button>
              </div>

              <div v-if="signers.length">
                <div
                  class="doc-signer-card"
                  v-for="signer in signers"
                  :key="signer.id"
                >
                  <div
                    class="doc-signer-card__avatar"
                    :class="'doc-signer-card__avatar--' + signer.status"
                  >
                    {{ getInitials(signer.name) }}
                  </div>
                  <div class="doc-signer-card__info">
                    <p class="doc-signer-card__name">{{ signer.name }}</p>
                    <p class="doc-signer-card__email">{{ signer.email }}</p>
                    <p class="doc-signer-card__role" v-if="signer.role">
                      {{ signer.role }}
                    </p>
                  </div>
                  <div class="doc-signer-card__right">
                    <span
                      class="doc-signer-status"
                      :class="'doc-signer-status--' + signer.status"
                    >
                      {{
                        signer.read_status || readSignerStatus(signer.status)
                      }}
                    </span>
                    <el-dropdown
                      trigger="click"
                      @command="handleSignerCommand($event, signer)"
                      v-if="document.status === 'draft'"
                    >
                      <span class="doc-signer-card__menu">
                        <i class="el-icon-more" />
                      </span>
                      <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item command="delete">
                          <span style="color:#e24b4a">Eliminar</span>
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                  </div>
                </div>
              </div>
              <p v-else class="doc-signers-panel__empty">
                No se han agregado firmantes.
              </p>
            </div>

            <!-- Acciones rápidas -->
            <div class="doc-actions-panel mt-3">
              <p class="doc-actions-panel__title">Acciones</p>
              <button class="doc-actions-panel__btn" @click="duplicateDocument">
                <i class="el-icon-document-copy" /> Duplicar documento
              </button>
              <button
                class="doc-actions-panel__btn"
                v-if="
                  ['signed', 'downloaded', 'owner_signed'].includes(
                    document.status
                  )
                "
                @click="downloadPdf"
              >
                <i class="el-icon-download" /> Descargar PDF firmado
              </button>
              <button
                class="doc-actions-panel__btn"
                v-if="
                  ['signed', 'downloaded', 'owner_signed'].includes(
                    document.status
                  )
                "
                @click="validateDoc"
              >
                <i class="el-icon-document-checked" /> Validar firmas
              </button>
            </div>
          </b-col>
        </b-row>
      </b-container>

      <!-- Dialog agregar firmante -->
      <el-dialog
        :visible.sync="showAddSigner"
        title="Agregar firmante"
        width="440px"
        :close-on-click-modal="false"
      >
        <el-form
          :model="signerForm"
          :rules="signerRules"
          ref="signerForm"
          label-position="top"
          size="small"
        >
          <el-form-item label="Nombre" prop="name">
            <el-input v-model="signerForm.name" placeholder="Nombre completo" />
          </el-form-item>
          <el-form-item label="Email" prop="email">
            <el-input
              v-model="signerForm.email"
              placeholder="correo@ejemplo.com"
            />
          </el-form-item>
          <el-form-item label="Rol (opcional)">
            <el-input
              v-model="signerForm.role"
              placeholder="Ej: Representante legal"
            />
          </el-form-item>
          <el-form-item label="Método de firma">
            <el-select v-model="signerForm.sign_method" style="width:100%">
              <el-option label="Firma dibujada" value="drawn" />
              <el-option
                label="Certificado digital (.p12)"
                value="certificate"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="Orden de firma">
            <el-input-number
              v-model="signerForm.sign_order"
              :min="1"
              :max="20"
            />
          </el-form-item>
        </el-form>
        <span slot="footer">
          <button
            class="btn md__btn-third-secondary mr-2"
            @click="showAddSigner = false"
          >
            Cancelar
          </button>
          <button
            class="btn md__btn-primary"
            :disabled="addingSigner"
            @click="addSigner"
          >
            <i class="el-icon-loading" v-if="addingSigner" />
            Agregar
          </button>
        </span>
      </el-dialog>

      <!-- Modal: Revisión antes de enviar -->
      <DocumentSendReview
        :show.sync="showSendReview"
        :document="document"
        :signers="signers"
        :sending="sendingDocument"
        @send="confirmSendDocument"
        @cancel="showSendReview = false"
      />

      <!-- Modal: Firma del dueño -->
      <SignConfirmModal
        :show.sync="showOwnerSign"
        :signer="ownerSignerInfo"
        :document-name="document ? document.name : ''"
        :submitting="signingAsOwner"
        @confirm="submitOwnerSignature"
        @cancel="showOwnerSign = false"
      />
    </template>
  </div>
</template>

<script>
import TopBarTitleComponent from "../../components/TopBarTitle";
import ButtonPrimaryComponent from "../../components/ButtonPrimary";
import SkeletonLoadingCard from "@/components/Animations/Loading/SkeletonCard";
import documentoService from "../services/documentoService";
import DocumentSendReview from "../components/DocumentSendReview";
import SignConfirmModal from "../components/SignConfirmModal";

export default {
  name: "DocumentDetailPage",
  components: {
    TopBarTitleComponent,
    ButtonPrimaryComponent,
    SkeletonLoadingCard,
    DocumentSendReview,
    SignConfirmModal
  },
  data() {
    return {
      loading: true,
      document: null,
      signers: [],
      auditLogs: [],
      showAddSigner: false,
      addingSigner: false,
      // Send review
      showSendReview: false,
      sendingDocument: false,
      // Owner sign
      showOwnerSign: false,
      signingAsOwner: false,
      // PDF preview
      showPreview: false,
      pdfPages: [],
      loadingPdf: false,
      // Signer form
      signerForm: {
        name: "",
        email: "",
        role: "",
        sign_method: "drawn",
        sign_order: 1
      },
      signerRules: {
        name: [
          { required: true, message: "Ingresa el nombre", trigger: "blur" }
        ],
        email: [
          { required: true, message: "Ingresa el email", trigger: "blur" },
          { type: "email", message: "Email inválido", trigger: "blur" }
        ]
      }
    };
  },
  computed: {
    signedCount() {
      if (!this.document || !this.document.signers) return 0;
      return this.document.signers.filter(s => s.status === "signed").length;
    },
    signProgress() {
      if (
        !this.document ||
        !this.document.signers ||
        !this.document.signers.length
      )
        return 0;
      return Math.round(
        (this.signedCount / this.document.signers.length) * 100
      );
    },
    canOwnerSign() {
      return false; // Owner now signs in the wizard flow before sending
    },
    ownerSignerInfo() {
      const user = this.$store.getters.getUser;
      return {
        name: user ? user.name : "",
        email: user ? user.email : ""
      };
    }
  },
  methods: {
    readStatus(status) {
      const map = {
        draft: "Borrador",
        owner_signed: "Firmado por ti",
        sent: "Enviado",
        viewed: "Visto",
        partially_signed: "Parcialmente firmado",
        signed: "Completado",
        downloaded: "Descargado",
        expired: "Expirado",
        cancelled: "Cancelado"
      };
      return map[status] || status;
    },
    readType(type) {
      const map = {
        contrato: "Contrato",
        nda: "NDA",
        propuesta: "Propuesta",
        alcance: "Alcance técnico",
        otro: "Otro"
      };
      return map[type] || type;
    },
    readSource(source) {
      const map = {
        template: "Plantilla",
        scratch: "Desde cero",
        upload: "PDF cargado"
      };
      return map[source] || source;
    },
    readSignerStatus(status) {
      const map = {
        pending: "Pendiente",
        sent: "Enviado",
        viewed: "Visto",
        signed: "Firmado",
        declined: "Rechazado"
      };
      return map[status] || status;
    },
    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map(w => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    getTimelineDotClass(action) {
      if (
        action === "signed" ||
        action === "completed" ||
        action === "owner_signed"
      )
        return "doc-timeline__dot--green";
      if (
        action === "declined" ||
        action === "expired" ||
        action === "cancelled"
      )
        return "doc-timeline__dot--red";
      if (
        action === "sent" ||
        action === "viewed" ||
        action === "reminder_sent"
      )
        return "doc-timeline__dot--yellow";
      return "";
    },

    async loadDocument() {
      try {
        const response = await documentoService.getDocument(
          this.$route.params.id
        );
        const { success, data } = response.data;
        if (success) {
          this.document = data;
          this.signers = data.signers || [];
          this.auditLogs = data.audit_logs || [];
        } else {
          this.$notifications.error({ message: "Documento no encontrado" });
          this.$router.push({ name: "documentos" });
        }
      } catch (e) {
        this.$notifications.error({ message: "Error al cargar el documento" });
        this.$router.push({ name: "documentos" });
      } finally {
        this.loading = false;
      }
    },

    async cancelDocument() {
      try {
        await this.$confirm(
          "¿Cancelar este documento? Los firmantes pendientes no podrán firmar.",
          "Cancelar documento",
          {
            confirmButtonText: "Sí, cancelar",
            cancelButtonText: "No",
            type: "warning"
          }
        );
        const response = await documentoService.cancelDocument(
          this.document.id
        );
        if (response.data.success) {
          this.$notifications.success({ message: "Documento cancelado" });
          this.loadDocument();
        } else {
          this.$notifications.error({
            message: response.data.message || "Error al cancelar"
          });
        }
      } catch (e) {
        if (e !== "cancel") {
          this.$notifications.error({
            message: "Error al cancelar el documento"
          });
        }
      }
    },

    goEdit() {
      this.$router.push({
        name: "documentos.edit",
        params: { id: this.document.id }
      });
    },

    openSendReview() {
      if (!this.signers.length) {
        this.$notifications.warning({
          message: "Agrega al menos un firmante antes de enviar"
        });
        return;
      }
      this.showSendReview = true;
    },

    async confirmSendDocument(options) {
      this.sendingDocument = true;
      try {
        const response = await documentoService.sendDocument(
          this.document.id,
          options
        );
        const { success } = response.data;
        if (success) {
          this.$notifications.success({
            message: "Documento enviado a firmar"
          });
          this.showSendReview = false;
          this.loadDocument();
        } else {
          this.$notifications.error({
            message: response.data.message || "Error al enviar"
          });
        }
      } catch (e) {
        this.$notifications.error({ message: "Error al enviar" });
      } finally {
        this.sendingDocument = false;
      }
    },

    async submitOwnerSignature(payload) {
      this.signingAsOwner = true;
      try {
        const response = await documentoService.signOwnerDocument(
          this.document.id,
          payload
        );
        const { success } = response.data;
        if (success) {
          this.$notifications.success({ message: "Documento firmado" });
          this.showOwnerSign = false;
          this.loadDocument();
        } else {
          this.$notifications.error({
            message: response.data.message || "Error al firmar"
          });
        }
      } catch (e) {
        this.$notifications.error({ message: "Error al firmar el documento" });
      } finally {
        this.signingAsOwner = false;
      }
    },

    loadPdfScript() {
      return new Promise((resolve, reject) => {
        if (window.pdfjsLib) return resolve(window.pdfjsLib);
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
        script.onload = () => {
          const lib = window.pdfjsLib;
          lib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
          resolve(lib);
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    async loadPdfPreview() {
      if (this.pdfPages.length || this.loadingPdf) return;
      this.loadingPdf = true;
      try {
        const response = await documentoService.previewPdf(this.document.id);
        if (!response.data || !response.data.byteLength) return;

        const pdfjsLib = await this.loadPdfScript();
        const typedArray = new Uint8Array(response.data);
        const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;

        this.pdfPages = Array.from({ length: pdf.numPages }, (_, i) => i + 1);
        await this.$nextTick();

        for (let i = 0; i < pdf.numPages; i++) {
          const page = await pdf.getPage(i + 1);
          const scale = 1.5;
          const viewport = page.getViewport({ scale });
          const canvasRef = this.$refs["pdfCanvas" + i];
          if (!canvasRef || !canvasRef[0]) continue;
          const canvas = canvasRef[0];
          const ctx = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: ctx, viewport }).promise;
        }
      } catch (e) {
        this.pdfPages = [];
      } finally {
        this.loadingPdf = false;
      }
    },

    async addSigner() {
      this.$refs.signerForm.validate(async valid => {
        if (!valid) return;
        this.addingSigner = true;
        try {
          const response = await documentoService.addSigner(
            this.document.id,
            this.signerForm
          );
          const { success } = response.data;
          if (success) {
            this.$notifications.success({ message: "Firmante agregado" });
            this.showAddSigner = false;
            this.signerForm = {
              name: "",
              email: "",
              role: "",
              sign_method: "drawn",
              sign_order: this.signers.length + 1
            };
            this.loadDocument();
          } else {
            this.$notifications.error({
              message: response.data.message || "Error al agregar"
            });
          }
        } catch (e) {
          this.$notifications.error({ message: "Error al agregar firmante" });
        } finally {
          this.addingSigner = false;
        }
      });
    },

    async handleSignerCommand(command, signer) {
      if (command === "delete") {
        try {
          await this.$confirm("¿Eliminar este firmante?", "Confirmar", {
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
            type: "warning"
          });
          await documentoService.deleteSigner(signer.id);
          this.$notifications.success({ message: "Firmante eliminado" });
          this.loadDocument();
        } catch (e) {
          if (e !== "cancel") {
            this.$notifications.error({ message: "Error al eliminar" });
          }
        }
      }
    },

    async downloadPdf() {
      try {
        const response = await documentoService.downloadDocument(
          this.document.id
        );
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.document.name}_firmado.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$notifications.error({ message: "Error al descargar" });
      }
    },

    async duplicateDocument() {
      try {
        await documentoService.duplicateDocument(this.document.id);
        this.$notifications.success({ message: "Documento duplicado" });
        this.$router.push({ name: "documentos" });
      } catch (e) {
        this.$notifications.error({ message: "Error al duplicar" });
      }
    },

    async validateDoc() {
      try {
        const response = await documentoService.validateDocument(
          this.document.id
        );
        if (response.data.success) {
          const result = response.data.data;
          if (result.has_signatures && result.valid) {
            this.$alert(
              `Se encontraron ${result.signature_count} firma(s) válidas en el documento.`,
              "Validación exitosa",
              { confirmButtonText: "OK", type: "success" }
            );
          } else if (result.has_signatures && !result.valid) {
            this.$alert(
              "Se encontraron firmas inválidas en el documento.",
              "Validación con advertencias",
              { confirmButtonText: "OK", type: "warning" }
            );
          } else {
            this.$alert(
              result.message || "El documento no contiene firmas digitales.",
              "Sin firmas",
              { confirmButtonText: "OK", type: "info" }
            );
          }
        }
      } catch (e) {
        this.$notifications.error({ message: "Error al validar el documento" });
      }
    }
  },
  watch: {
    showPreview(val) {
      if (val && this.document && this.document.source === "upload") {
        this.loadPdfPreview();
      }
    }
  },
  mounted() {
    this.loadDocument();
  }
};
</script>

<style lang="scss" scoped>
/* ─── Summary card ───────────────────────────────────────────────── */
.doc-detail-summary {
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 0 10px 0 #d6e4f3;
}
.doc-detail-summary__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #e4eaf6;
  &:last-child {
    border-bottom: none;
  }
}
.doc-detail-summary__label {
  font-size: 13px;
  color: #989aae;
}
.doc-detail-summary__value {
  font-size: 13px;
  color: #31355c;
  font-weight: 500;
}

/* ─── Badge ──────────────────────────────────────────────────────── */
.doc-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  white-space: nowrap;
  &--draft {
    background: #e4eaf6;
    color: #7e819f;
  }
  &--sent,
  &--viewed,
  &--partially_signed {
    background: #faeeda;
    color: #854f0b;
  }
  &--signed {
    background: #e1f5ee;
    color: #0f6e56;
  }
  &--expired {
    background: #fcebeb;
    color: #a32d2d;
  }
  &--cancelled {
    background: #e4eaf6;
    color: #7e819f;
  }
}

/* ─── Progress ───────────────────────────────────────────────────── */
.doc-detail-progress {
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 0 10px 0 #d6e4f3;
}
.doc-detail-progress__title {
  font-size: 15px;
  font-weight: 700;
  color: #1e1c42;
  margin: 0 0 12px;
}
.doc-detail-progress__bar-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}
.doc-detail-progress__bar {
  flex: 1;
  height: 8px;
  background: #e4eaf6;
  border-radius: 4px;
  overflow: hidden;
}
.doc-detail-progress__fill {
  height: 100%;
  background: #3fdbbf;
  border-radius: 4px;
  transition: width 0.4s ease;
}
.doc-detail-progress__text {
  font-size: 12px;
  color: #989aae;
  white-space: nowrap;
}

/* ─── Preview panel ──────────────────────────────────────────────── */
.doc-preview-panel {
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 0 10px 0 #d6e4f3;
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
  }
  &__title {
    font-size: 15px;
    font-weight: 700;
    color: #1e1c42;
    margin: 0;
  }
  &__toggle {
    border: none;
    background: none;
    color: #3fdbbf;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 6px;
    i {
      margin-left: 4px;
    }
    &:hover {
      background: #e6faf5;
    }
  }
  &__html {
    font-size: 14px;
    color: #31355c;
    line-height: 1.7;
    max-height: 500px;
    overflow-y: auto;
    padding: 16px;
    background: #fafbfd;
    border: 1px solid #e4eaf6;
    border-radius: 10px;
  }
  &__page {
    margin-bottom: 12px;
    border: 1px solid #e4eaf6;
    border-radius: 8px;
    overflow: hidden;
    canvas {
      display: block;
      width: 100%;
      height: auto;
    }
  }
  &__loading,
  &__empty {
    text-align: center;
    padding: 30px 0;
    color: #989aae;
    font-size: 13px;
    i {
      font-size: 24px;
      margin-bottom: 8px;
      display: block;
    }
  }
}

/* ─── Timeline ───────────────────────────────────────────────────── */
.doc-timeline {
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 0 10px 0 #d6e4f3;
}
.doc-timeline__title {
  font-size: 15px;
  font-weight: 700;
  color: #1e1c42;
  margin: 0 0 16px;
}
.doc-timeline__list {
  position: relative;
  padding-left: 20px;
  &::before {
    content: "";
    position: absolute;
    left: 5px;
    top: 6px;
    bottom: 6px;
    width: 2px;
    background: #e4eaf6;
  }
}
.doc-timeline__item {
  position: relative;
  padding-bottom: 16px;
  &:last-child {
    padding-bottom: 0;
  }
}
.doc-timeline__dot {
  position: absolute;
  left: -20px;
  top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #c4c6d4;
  border: 2px solid #fff;
  z-index: 1;
  &--green {
    background: #3fdbbf;
  }
  &--red {
    background: #e24b4a;
  }
  &--yellow {
    background: #f59e0b;
  }
}
.doc-timeline__action {
  font-size: 13px;
  color: #31355c;
  margin: 0;
  font-weight: 500;
}
.doc-timeline__meta {
  font-size: 11px;
  color: #989aae;
  margin: 2px 0 0;
}
.doc-timeline__empty {
  font-size: 13px;
  color: #989aae;
  margin: 0;
}

/* ─── Signers panel ──────────────────────────────────────────────── */
.doc-signers-panel {
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 0 10px 0 #d6e4f3;
}
.doc-signers-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.doc-signers-panel__title {
  font-size: 15px;
  font-weight: 700;
  color: #1e1c42;
  margin: 0;
}
.doc-signers-panel__add {
  border: none;
  background: none;
  color: #3fdbbf;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 6px;
  &:hover {
    background: #e6faf5;
  }
}
.doc-signers-panel__empty {
  font-size: 13px;
  color: #989aae;
  margin: 0;
}

/* ─── Signer card ────────────────────────────────────────────────── */
.doc-signer-card {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e4eaf6;
  &:last-child {
    border-bottom: none;
  }
}
.doc-signer-card__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  margin-right: 10px;
  flex-shrink: 0;
  background: #e4eaf6;
  color: #7e819f;
  &--signed {
    background: #e1f5ee;
    color: #0f6e56;
  }
  &--declined {
    background: #fcebeb;
    color: #a32d2d;
  }
  &--sent,
  &--viewed {
    background: #faeeda;
    color: #854f0b;
  }
}
.doc-signer-card__info {
  flex: 1;
  min-width: 0;
}
.doc-signer-card__name {
  font-size: 13px;
  font-weight: 500;
  color: #31355c;
  margin: 0;
}
.doc-signer-card__email {
  font-size: 11px;
  color: #989aae;
  margin: 2px 0 0;
}
.doc-signer-card__role {
  font-size: 11px;
  color: #7e819f;
  margin: 2px 0 0;
}
.doc-signer-card__right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: 8px;
}
.doc-signer-card__menu {
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  &:hover {
    background: #e4eaf6;
  }
}

.doc-signer-status {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
  &--pending {
    background: #e4eaf6;
    color: #7e819f;
  }
  &--sent,
  &--viewed {
    background: #faeeda;
    color: #854f0b;
  }
  &--signed {
    background: #e1f5ee;
    color: #0f6e56;
  }
  &--declined {
    background: #fcebeb;
    color: #a32d2d;
  }
}

/* ─── Actions panel ──────────────────────────────────────────────── */
.doc-actions-panel {
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 0 10px 0 #d6e4f3;
}
.doc-actions-panel__title {
  font-size: 15px;
  font-weight: 700;
  color: #1e1c42;
  margin: 0 0 10px;
}
.doc-actions-panel__btn {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  color: #31355c;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #e4eaf6;
  }
  i {
    margin-right: 6px;
    color: #989aae;
  }
}
</style>
